import type { UserSchema } from '@takeshape/schema';
import { useQuery } from '@tanstack/react-query';
import { useActiveSchema } from 'app/branches/hooks/use-active-schema';
import useHotkey from 'app/common/hooks/use-hotkey';
import { getCurrentProjectId } from 'app/project/selectors';
import type { SchemaIndex } from 'app/schema/types';
import buildSchemaIndex from 'app/schema/util/build-schema-index';
import { searchQuery } from 'app/universal-search/api';
import type { SearchForm } from 'app/universal-search/types';
import { dataResultToSearchResult, schemaIndexEntryToSearchResult } from 'app/universal-search/util/normalize-results';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useRouter from '../../../common/hooks/use-router';
import { SchemaView } from '../../../schema';
import type { SearchHookOwnProps } from './search';

const SCHEMA_RESULT_SIZE = 15;

export function useSchemaSearch(userSchema?: UserSchema | null, query?: string): SchemaIndex {
  const schemaIndex = useMemo(
    () => (userSchema ? buildSchemaIndex({ userSchema, view: SchemaView.List }) : []),
    [userSchema]
  );
  if (!query) return schemaIndex.slice(0, SCHEMA_RESULT_SIZE);
  return schemaIndex
    .filter((entry) => entry.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, SCHEMA_RESULT_SIZE);
}

export function useSearch(props: SearchHookOwnProps) {
  const { open: initialOpen } = props;
  const projectId = useSelector(getCurrentProjectId);
  const { activeSchema } = useActiveSchema();
  const [query, setQuery] = useState<string>();
  const [open, setOpen] = useState(Boolean(initialOpen));
  const router = useRouter();

  const onSubmit = useCallback(({ query }: SearchForm) => {
    setQuery(query);
  }, []);

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  useHotkey('metaKey', 'k', openModal);

  const { data, isLoading } = useQuery([projectId, 'search', query], async () => searchQuery({ query }), {
    enabled: open
  });

  const schemaResults = useSchemaSearch(activeSchema, query);
  const contentResults = data?.search?.results;
  const combinedResults = schemaResults
    .map(schemaIndexEntryToSearchResult)
    .concat(contentResults?.map(dataResultToSearchResult) ?? []);

  const displaySearch = router.pathname.startsWith('/project/');

  return {
    displaySearch,
    open,
    query,
    results: combinedResults,
    isLoading,
    onSubmit,
    openModal,
    closeModal
  };
}

export type SearchHookProps = ReturnType<typeof useSearch>;
